import {Component, EventEmitter, Input, Output, ViewEncapsulation} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {IdName} from '../../models/id-name.model';

@Component({
  selector: 'si-radio-dynamic',
  templateUrl: './radio-dynamic.component.html',
  styleUrls: ['./radio-dynamic.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RadioDynamicComponent {
  @Input() text: string;
  @Input() styleClass: string;
  @Input() id: string;
  @Input() vertical = false;

  // Name of the attribute that we put in the model.
  // If we initialize the model variable with a value other than an object (such as a 'code' attribute), we must put 'code' in this input.
  @Input() valueAttrInput: string;
  @Input() valueAttr: string;
  @Input() disabled: boolean;
  @Input() tooltipText: string;
  @Input() tooltipPosition: string;
  @Input() tooltipIcon: string;
  @Input() tooltipStyleClass: string;
  @Input() tooltipEscape = true;
  @Input() forceShowTooltipIcon: boolean;
  @Input() hideLabel = false;
  @Output() modelChange = new EventEmitter<any>();
  @Output() tooltipIconClick = new EventEmitter<any>();
  options: IdName[];
  private _path: string;
  private _values: IdName[];
  private _model: any;
  modelInputToLoad: number | string;

  constructor(private http: HttpClient) {
  }

  get model(): any {
    return this._model;
  }

  @Input()
  set model(val) {
    if (this.newModelIsEqualToCurrentModel(val)) {
      return;
    }

    if (val == null || val instanceof Object) {
      this._model = val;
    } else {
      if (this.options?.length) {
        this._model = this.options.find(option => option[this.valueAttrInput || this.valueAttr || 'id'] === val);
      } else {
        this.modelInputToLoad = val;
      }
    }
  }

  get path(): string {
    return this._path;
  }

  @Input()
  set path(val) {
    this._path = val;
    this.getOptions();
  }

  get values(): IdName[] {
    return this._values;
  }

  @Input()
  set values(val) {
    this._values = val;
    this.options = val;
    this.setDefaultOption();
  }

  newModelIsEqualToCurrentModel(val: any): boolean {
    // When the component has valueAttr, the model emitted only has the parameter specified in valueAttr.
    // So, we do not want to update the internal model when the model is updated with the valueAttr parameter only.
    if (this._model && val != null && this.valueAttr && !(val instanceof Object) && val === this._model[this.valueAttr]) {
      return true;
    }

    // Early return if val has the same value as the model.
    const attrToRead = this.valueAttrInput || this.valueAttr || 'id';
    if (this._model && val != null && val instanceof Object && val[attrToRead] === this._model[attrToRead]) {
      return true;
    }

    return false;
  }

  async getOptions(): Promise<void> {
    this.options = [];
    try {
      this.options = await this.http.get<IdName[]>(this.path).toPromise();
      this.setDefaultOption();
    } catch (error) {
      console.error(error);
    }
  }

  setDefaultOption(): void {
    if (this.model || !this.options?.length) {
      return;
    }

    if (this.modelInputToLoad) {
      this._model = this.options.find(option => option[this.valueAttrInput || this.valueAttr || 'id'] === this.modelInputToLoad);
      this.modelInputToLoad = null;
      return;
    }

    this._model = this.options.find((option) => option.isDefault) || this.options[0];
    this.onChangeModel(this.model);
  }

  onChangeModel(model): void {
    setTimeout(() => {
      if (this.valueAttr) {
        this.modelChange.emit(model[this.valueAttr]);
      } else {
        this.modelChange.emit(model);
      }
    });
  }

  onClickTooltipIcon(event: any): void {
    this.tooltipIconClick.emit(event);
  }
}
